import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Loader2, ShieldAlert, Eye, BarChart3, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../contexts/AuthContext';

const levelStyle = (lv) => {
  if (lv === 'grave') return { bg: '#dc2626', t: 'GRAVE' };
  if (lv === 'medio') return { bg: '#f59e0b', t: 'MEDIO' };
  return { bg: '#facc15', t: 'LIEVE' };
};

const DisciplinaPage = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/warnings/summary');
        setSummary(data || []);
      } catch (e) {
        setSummary([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const totalActive = summary.reduce((a, b) => a + (b.count || 0), 0);
  const underObsCount = summary.filter(t => t.under_observation).length;

  return (
    <div className="space-y-6" data-testid="disciplina-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 flex items-center gap-2">
          <ShieldAlert className="w-7 h-7 text-amber-600" /> Disciplina Tecnici
        </h1>
        <p className="text-sm text-slate-500 mt-1">Quadro mensile dei cartellini di ammonizione attivi. Soglia &quot;sotto osservazione&quot;: 3 cartellini/mese.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card data-testid="stat-active"><CardContent className="pt-4"><div className="text-[10px] uppercase font-bold text-slate-500">Cartellini attivi (mese)</div><div className="text-4xl font-extrabold text-amber-600">{totalActive}</div></CardContent></Card>
        <Card data-testid="stat-under-obs"><CardContent className="pt-4"><div className="text-[10px] uppercase font-bold text-slate-500">Tecnici sotto osservazione</div><div className="text-4xl font-extrabold text-red-600">{underObsCount}</div></CardContent></Card>
        <Card data-testid="stat-techs"><CardContent className="pt-4"><div className="text-[10px] uppercase font-bold text-slate-500">Tecnici con cartellini</div><div className="text-4xl font-extrabold text-slate-900">{summary.length}</div></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg font-bold">Tecnici sanzionati nel mese corrente</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : summary.length === 0 ? (
            <div className="text-center py-10 text-slate-500">
              <ShieldAlert className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm font-semibold">Nessun cartellino emesso questo mese</p>
              <p className="text-xs text-slate-400 mt-1">Tutto sotto controllo.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="disciplina-list">
              {summary.map((t) => {
                const isObs = t.under_observation;
                const breakdown = (t.levels || []).reduce((acc, l) => { acc[l] = (acc[l] || 0) + 1; return acc; }, {});
                return (
                  <div
                    key={t.technician_id}
                    className="flex items-center gap-3 p-4 rounded-lg border-2 transition hover:shadow-md cursor-pointer"
                    style={{
                      background: isObs ? 'rgba(220, 38, 38, 0.06)' : 'rgba(245, 158, 11, 0.05)',
                      borderColor: isObs ? '#dc2626' : '#e2e8f0',
                    }}
                    onClick={() => navigate(`/performance/${t.technician_id}`)}
                    data-testid={`disciplina-row-${t.technician_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-slate-900 truncate">{t.technician_name}</span>
                        {isObs && (
                          <Badge className="bg-red-600 text-white text-[10px] flex items-center gap-1" data-testid={`badge-obs-${t.technician_id}`}>
                            <Eye className="w-3 h-3" /> SOTTO OSSERVAZIONE
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        {Object.entries(breakdown).map(([lv, cnt]) => {
                          const st = levelStyle(lv);
                          return (
                            <span key={lv} className="px-2 py-0.5 rounded text-[10px] font-bold text-white" style={{ background: st.bg }}>
                              {cnt}× {st.t}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-3xl font-extrabold" style={{ color: isObs ? '#dc2626' : '#0f172a' }}>{t.count}</div>
                      <div className="text-[10px] uppercase text-slate-500 font-semibold">questo mese</div>
                    </div>
                    <Button variant="ghost" size="sm" className="shrink-0" data-testid={`disciplina-view-${t.technician_id}`}>
                      <BarChart3 className="w-4 h-4 mr-1" /> Performance <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DisciplinaPage;
